import Client from '@/models/Client'
import Order from '@/models/Order'
import OrderItem from '@/models/OrderItem'
import CashRegisterTransaction from '@/models/CashRegisterTransaction'
import Shift from '@/models/Shift'

import i18n from '@/i18n';
import store from '@/store';

import TextGen from '@/utilities/TextGen'
import latToCyr from '@/utilities/LatToCyr'
import Money from '@/wrappers/Money';
import EinvoiceService from '@/services/EinvoiceService';

export default class PrintGen {

    constructor(charsPerLine = 32) {
        this.charsPerLine = charsPerLine
        this.textGen = new TextGen(charsPerLine)
    }

    orderToLines(order, options = {}) {
        if(locale === 'sr') {
            if(order.isUnpaid() || !options.isReceipt) {
                return this.unpaidOrderToLinesSr(order, options);
            }

            return this.orderToLinesSr(order)
        }

        return this.orderToLinesMe(order, options)
    }

    orderToLinesMe(order, options = {}) {
        const priceFormat = '0,0.00'
        let client = order.client || Client.find(order.client_id)

        const venue = store.getters.tenant.venue

        const receiptNote = venue?.receipt_note?.split('|')[0] || '';
        const receiptHeaderText = venue?.receipt_note?.split('|')[1] || '';

        const lines = []

        // If not a receipt
        if(!options.isReceipt) {
            const curShift = Shift.current();
            const curShiftOrders = curShift ? curShift.getOrders() : [];
            const orderNumber = curShiftOrders.length + 1

            if(options.printingSector) {
                lines.push(`[C]${orderNumber} # ${i18n.t('products.printing_sector.' + options.printingSector)}`)
                lines.push('[L]')
            }

            lines.push('[L][D-]')

            if(order.getTable()) {
                lines.push(`[L]${i18n.t('general.table')}: ${order.getTable().name}`)
            }

            lines.push(`[L]${i18n.t('general.time')}: ${this.formatDateTimeHuman(order.getLastActivityAt())}`)
            lines.push(`[L]${i18n.t('general.issued_by')}:[B]${order.getUser().name}`)
            lines.push('[L][D-]')

            // Product Items
            let productItems = order.isSummary()
                ? order.paysForOrders().flatMap(order => order.items)
                : order.items

            productItems = productItems.map((item, index) => {
                item.index = index;
                return item;
            })

            // Item separators
            const separators = order.getItemSeparators();

            if(options.printingSector && options.printingSector !== 'order') {
                productItems = productItems.filter(item => {
                    const product = item.getProduct();
                    return !product || product.printing_sector === options.printingSector
                })
            }

            lines.push('[L]')

            productItems.forEach((item, index) => {
                if(index !== 0 && separators.length && separators[0] <= item.index) {
                    lines.push('[L][D-]');
                    lines.push('[L]')
                    separators.shift();
                }

                lines.push(`[H22][L]${item.getCount()} x ${item.name}`)

                if(item.comments.length) {
                    lines.push('[L]')
                }

                item.comments.forEach(comment => {
                    lines.push(`[L]- ${comment}`)
                })

                lines.push('[L]')
            });

            return lines
        }

        // Receipt Header
        lines.push('[L][D=]')
        lines.push(`[C]${venue.company_name}`)
        lines.push(`[C]${venue.name}`)

        if(venue.tax_id_number && venue.tax_id_number.length !== 13) {
            lines.push(`[C]${i18n.t('general.tax_id_number')}: ${venue.tax_id_number}`)
        }

        lines.push(`[C]${venue.address}`)

        if(receiptHeaderText) {
            receiptHeaderText.split('\n').forEach(l => lines.push(`[C]${l}`))
        }

        if(order.isProforma()) {
            lines.push(`[C]${i18n.tc('general.proforma', 1)}`)
        } else if(options.isCopy) {
            lines.push(`[C]${i18n.t('general.receipt_copy')}`)
        }

        lines.push('[L][D=]')

        // Client
        if(client && client.name) {
            lines.push(`[L]${i18n.tc('general.buyer', 1)}: ${client.name}`)
            if(client.address) {
                lines.push(`[L]${client.address}${client.city ? (', ' + client.city) : ''}${client.country ? (', ' + client.country) : ''}`)
            }
            let taxIdNumberPrinted = false;
            if((client.country && client.country !== 'MNE') && client.id_number) {
                const idType = i18n.t(`general.${client.id_type}`)
                lines.push(`[L]${idType}: ${client.id_number}`)
                taxIdNumberPrinted = client.id_type === 'tax_id_number';
            }
            if(!taxIdNumberPrinted && client.tax_id_number) {
                lines.push(`[L]${i18n.t('general.tax_id_number')}: ${client.tax_id_number}`)
            }
            if(client.tax_id_card_number) {
                lines.push(`[L]${i18n.t('general.tax_id_card_number')}: ${client.tax_id_card_number}`)
            }
            lines.push('[L][D-]')
        }

        // Receipt Subheader
        if(order.internal_number) {
            lines.push(`[L]${!order.isProforma() ? i18n.t('invoice.number') : i18n.t('invoice.proforma.number')}:[B]${order.internal_number}`)
        }

        if(order.number) {
            if(locale === 'me') {
                lines.push(`[L]${i18n.t('general.receipt_number_serial')}: ${order.number}`)
                lines.push(`[L]${i18n.t('general.receipt_number')}:[B]${order.getFullNumber()}`)
            } else {
                lines.push(`[L]${i18n.t('general.receipt_number')}: ${order.number}`)
            }
        }

        lines.push(`[L]${i18n.t('general.time')}: ${this.formatDateTime(order.getLastActivityAt())}`)

        if(!order.isIncoming()) {
            lines.push(`[L]${i18n.t('general.operator')}: ${order.getUser().name}`)
            lines.push(`[L]${i18n.t('general.operator_code')}: ${order.getUser().hash}`)
        }

        if(order.paymentType() && !order.isProforma()) {
            lines.push(`[L]${i18n.t('general.paid_by')}: ${order.paymentType()}`)
        }

        if(order.isSummary()) {
            lines.push(`[L]${i18n.t('general.receipt_type')}: ${i18n.t('general.receipt_summary')}`)
        } else if(order.isAdvance()) {
            lines.push(`[L]${i18n.t('general.receipt_type')}: ${i18n.t('general.advance')}`)
        }

        if(order.getTable()) {
            lines.push(`[L]${i18n.t('general.table')}: ${order.getTable().name}`)
        }

        // Product Items
        let productItems = order.isSummary()
            ? OrderItem.merge(order.paysForOrders().flatMap(order => order.items), order)
            : order.items

        if(productItems.length) {
            lines.push('[L][D-]')
            lines.push(`[L]${i18n.tc('general.item', 1)} (${i18n.t('tax_rates.rate')})[S]${i18n.t('general.price')}`)
            lines.push('[L][D-]')
        }

        const itemCommentsOnFiscalReceipts = !!venue?.settings?.item_comments_on_fiscal_receipts;

        productItems.forEach(item => {
            const count = item.getCount()
            const name = item.name
            const unitPrice = item.getUnitPriceAfterTax().toFormat(priceFormat)
            const price = item.getPrice().toFormat(priceFormat)
            const discountPercent = item.getDiscountPercent()
            const discountAmount = item.getDiscountAmount().multiply(-1).toFormat(priceFormat)
            const toPay = item.getToPay().toFormat(priceFormat)

            lines.push(`[L]${name} (${item.getTaxRate()}%)`)
            if(itemCommentsOnFiscalReceipts && item.comments.length) {
                item.comments.join('\n').split('\n').forEach(l => lines.push(`[L]${l}`))
            }

            lines.push(`[L]   ${count} x ${unitPrice}[S]${price}`)

            if(discountPercent > 0) {
                lines.push(`[L]   ${i18n.t('general.discount')} (${discountPercent}%)[S]${discountAmount}`)
                lines.push(`[L]   ${i18n.t('general.with_discount')}[S]${toPay}`)
            }
        });

        // Receipt items
        const receiptItems = order.isSummary() ? order.items : []
        if(receiptItems.length) {
            lines.push('[L][D-]')
            lines.push(`[L]${i18n.tc('general.receipt', 1)}[S]${i18n.t('general.price')}`)
            lines.push('[L][D-]')
        }

        receiptItems.forEach(item => {
            const count = item.getCount()
            const name = item.name
            const unitPrice = item.getUnitPriceAfterTax().toFormat(priceFormat)
            const toPay = item.getToPay().toFormat(priceFormat)

            lines.push(`[L]${name}`)
            lines.push(`[L]   ${count} x ${unitPrice}[S]${toPay}`)
        });

        const taxes = order.taxes.filter(taxItem => !!taxItem.id || (!taxItem.id && taxItem.label.includes('VAT_')))

        if(venue.taxes_enabled && taxes.length) {
            lines.push('[L][D-]')

            // Taxes and Total Tax
            taxes.filter(taxItem => !taxItem.id).forEach(taxItem => {
                lines.push(`[L]${i18n.t('order.exempt_from_tax')} ${taxItem.label.replace('VAT_', '')}:[S]${Money.parse(taxItem.price_before_tax).toFormat(priceFormat)}`);
            })

            taxes.filter(taxItem => !!taxItem.id).forEach(taxItem => {
                lines.push(`[L]${taxItem.label} ${i18n.t('general.price_pre_tax').toLowerCase()}:[S]${Money.parse(taxItem.price_before_tax).toFormat(priceFormat)}`);
                lines.push(`[L]${taxItem.label} (${parseFloat(taxItem.rate)}%):[S]${Money.parse(taxItem.value).toFormat(priceFormat)}`);
            })
        }

        lines.push('[L][D-]')

        // Tax Summary
        const summary = this.getOrderSummary(order)
        const paymentTypes = this.getOrderPaymentTypes(order)

        if(venue.taxes_enabled) {
            if(summary.exemptFromTax && summary.exemptFromTax.toUnit() > 0) {
                lines.push(`[L]${i18n.t('order.exempt_from_tax')}:[S]${summary.exemptFromTax.toFormat(priceFormat)}`)
            }

            if(summary.priceBeforeTax) {
                lines.push(`[L]${i18n.t('general.price_pre_tax_total')}:[S]${summary.priceBeforeTax.toFormat(priceFormat)}`)
            }

            lines.push(`[L]${i18n.t('general.total_tax')}:[S]${order.getTax().toFormat(priceFormat)}`)

            lines.push('[L][D-]')
        }

        // Price and Discount
        if(summary.price) {
            lines.push(`[L]${i18n.t('general.total')}:[S]${summary.price.toFormat(priceFormat)}`)
        }

        if(summary.discountAmount) {
            lines.push(`[L]${i18n.t('general.discount')}:[S]${summary.discountAmount.toFormat(priceFormat)}`)
        }

        if(summary.toPay) {
            lines.push('[L][D-]')
            lines.push(`[H22][L]${i18n.t('general.to_pay').toUpperCase()}:[S]${summary.toPay.toFormat(priceFormat)}`)
        }

        paymentTypes.forEach(type => {
            lines.push(`[L]${type.label}:[S]${type.value.toFormat(priceFormat)}`)
        })

        let note = order.note || '';
        if(!venue.taxes_enabled) {
            note = '* ' + i18n.t('order.exempt_from_tax_comment') + '\n\n' + note;
        }

        if(note) {
            lines.push('[L][D-]')
            note.split('\n').forEach(l => lines.push(`[L]${l}`))
        }

        lines.push('[L][D-]')

        if(order.getPaidInvoice().toUnit() > 0) {
            lines.push(`[C]${i18n.tc('general.buyer', 1)}`)
            lines.push('[L]')
            lines.push('[L]')
            lines.push('[L][D-]')
        }

        // Fiscal Codes
        if(locale === 'me' && !order.isProforma()) {
            if(order.correctsOrders().length) {
                lines.push(`[L]REF IKOF:[B]${order.correctsOrders()[0].getIic()}`)
            }

            lines.push(`[L]IKOF:[B]${order.getIic()}`)

            if(order.fiscal_code) {
                lines.push(`[L]JIKR:[B]${order.fiscal_code}`)
            }
        }

        if(receiptNote) {
            lines.push(`[L][D-]`)
            receiptNote.split('\n').forEach(l => lines.push(`[L]${l}`))
            lines.push(`[L][D-]`)
        }

        return lines
    }

    orderToLinesSr(order) {
        const priceFormat = '0,0.00'
        const invoice = order.register_request || order.getRegisterRequestSr();
        const sdcResponse = order.register_response;

        const venue = store.getters.tenant.venue;

        const receiptNote = venue?.receipt_note?.split('|')[0] || '';
        const receiptHeaderText = venue?.receipt_note?.split('|')[1] || '';

        const itemCommentsOnFiscalReceipts = !!venue?.settings?.item_comments_on_fiscal_receipts;

        if(!invoice || !invoice?.items?.length || !sdcResponse) {
            return [];
        }

        let totalPaid = invoice.payment.reduce((acc, cur) => acc.add(Money.parse(cur.amount)), Money.parse(0))
        if(sdcResponse.advancePaid) {
            totalPaid = totalPaid.add(Money.parse(sdcResponse.advancePaid));
        }

        const totalAmount = Money.parse(sdcResponse.totalAmount)
        const remaining = invoice.invoiceType === 'Advance'
            ? Money.parse(0)
            : Money.parse(totalAmount.toRoundedUnit(2)).subtract(Money.parse(totalPaid.toRoundedUnit(2)))

        const lines = []

        if(receiptHeaderText) {
            receiptHeaderText.split('\n').forEach(l => lines.push(`[C]${l}`))
            lines.push(`[L]`)
        }

        if(['Normal','Advance'].includes(invoice.invoiceType)) {
            lines.push(`[C][D=] ${latToCyr(i18n.t('receipt.fiscal_receipt'))} `)
        } else {
            lines.push(`[H12][C][D=] ${latToCyr(i18n.t('receipt.nonfiscal_receipt'))} `)
        }

        lines.push(`[C]${sdcResponse.tin}`)
        lines.push(`[C]${sdcResponse.businessName}`)
        lines.push(`[C]${sdcResponse.locationName}`)
        lines.push(`[C]${sdcResponse.address}`)
        lines.push(`[C]${sdcResponse.district}`)

        lines.push(`[L][D-]`)

        if(invoice.cashier) {
            lines.push(`[L]${latToCyr(i18n.t('receipt.cashier'))}:[S]${invoice.cashier}`)
        }

        if(invoice.buyerId) {
            lines.push(`[L]${latToCyr(i18n.t('receipt.buyer_id'))}:[S]${invoice.buyerId}`)
        }

        if(invoice.buyerCostCenterId) {
            lines.push(`[L]${latToCyr(i18n.t('receipt.buyer_cost_center'))}:[S]${invoice.buyerCostCenterId}`)
        }

        lines.push(`[L]${latToCyr(i18n.t('receipt.number'))}:[S]${invoice.invoiceNumber}`)

        if(invoice.invoiceType === 'Advance' && invoice.dateAndTimeOfIssue) {
            lines.push(`[L]${latToCyr(i18n.t('receipt.time_pos'))}:[S]${moment(invoice.dateAndTimeOfIssue).tz('Europe/Belgrade').format('DD.MM.YYYY. HH:mm:ss')}`)
        }

        if(invoice.referentDocumentNumber) {
            lines.push(`[L]${latToCyr(i18n.t('receipt.ref_number'))}:[S]${invoice.referentDocumentNumber}`)
        }

        if(invoice.referentDocumentDT) {
            lines.push(`[L]${latToCyr(i18n.t('receipt.ref_time'))}:[S]${moment(invoice.referentDocumentDT).tz('Europe/Belgrade').format('DD.MM.YYYY. HH:mm:ss')}`)
        }

        lines.push('[C][D-] ' + latToCyr(i18n.t(`receipt.type_${invoice.invoiceType.toLowerCase()}`)) + ' ' + latToCyr(i18n.t(`receipt.transaction_${invoice.transactionType.toLowerCase()}`)) + ' ')

        lines.push(`[L]${latToCyr(i18n.t('receipt.items.heading'))}`)
        lines.push(`[L][D=]`)

        lines.push(`[L]${latToCyr(i18n.t('receipt.items.name'))}[C]${latToCyr(i18n.t('receipt.items.unit_price'))}[C]${latToCyr(i18n.t('receipt.items.quantity'))}[R]${latToCyr(i18n.t('receipt.items.to_pay'))}`)

        invoice.items.forEach(item => {
            const labels = item.labels.map(label => ` (${label})`).join('')
            const unit = item.unit ? '/' + item.unit : ''
            const gtin = item.gtin ? latToCyr('GTIN') + ': ' + item.gtin : ''
            lines.push(`[L]${item.name}${labels}${unit}`)

            if(itemCommentsOnFiscalReceipts && item.note) {
                item.note.split('\n').forEach(l => lines.push(`[L]${l}`))
            }

            if(gtin) {
                lines.push(`[L]${gtin}`)
            }
            const unitPrice = Money.parse(item.unitPrice).toFormat(priceFormat)
            const totalAmount = invoice.transactionType === 'Sale'
                ? Money.parse(item.totalAmount).toFormat(priceFormat)
                : Money.parse(item.totalAmount).multiply(-1).toFormat(priceFormat)

            lines.push(`[L][C]${unitPrice}[C]${item.quantity}[R]${totalAmount}`)
        })

        lines.push(`[L][D-]`)

        if(invoice.transactionType === 'Sale') {
            lines.push(`[L][H11]${latToCyr(i18n.t('receipt.payments.to_pay_sale'))}:[S]${Money.parse(sdcResponse.totalAmount).toFormat(priceFormat)}`)
        } else {
            lines.push(`[L][H11]${latToCyr(i18n.t('receipt.payments.to_pay_refund'))}:[S]${Money.parse(sdcResponse.totalAmount).toFormat(priceFormat)}`)
        }

        if(sdcResponse.advancePaid) {
            lines.push(`[L]${latToCyr(i18n.t('receipt.payments.advance'))}:[S]${Money.parse(sdcResponse.advancePaid).toFormat(priceFormat)}`)
        }

        if(!isNaN(sdcResponse.advanceTax)) {
            lines.push(`[L]${latToCyr(i18n.t('receipt.payments.advance_tax'))}:[S]${Money.parse(sdcResponse.advanceTax).toFormat(priceFormat)}`)
        }

        invoice.payment.forEach(payment => {
            const paymentType = payment.paymentType === 'MobileMoney' ? 'mobile_money' : payment.paymentType.toLowerCase();
            if(payment.amount != 0) {
                lines.push('[L]' + latToCyr(i18n.t(`receipt.payments.${paymentType}`)) + `:[S]${Money.parse(payment.amount).toFormat(priceFormat)}`)
            }
        })

        if(remaining.toUnit() < 0) {
            lines.push(`[L]${latToCyr(i18n.t('receipt.payments.change'))}:[S]${remaining.abs().toFormat(priceFormat)}`)
        } else if(remaining.toUnit() > 0) {
            lines.push(`[L]${latToCyr(i18n.t('receipt.payments.left_to_pay'))}:[S]${remaining.abs().toFormat(priceFormat)}`)
        }

        lines.push(`[L][D=]`)

        if(!['Normal','Advance'].includes(invoice.invoiceType)) {
            lines.push(`[H12][C]${latToCyr(i18n.t('receipt.nonfiscal_receipt'))}`)
            lines.push(`[L][D=]`)
        }

        lines.push(`[L]${latToCyr(i18n.t('receipt.taxes.label'))}[C]${latToCyr(i18n.t('receipt.taxes.name'))}[C]${latToCyr(i18n.t('receipt.taxes.rate'))}[R]${latToCyr(i18n.t('receipt.taxes.amount'))}`)
        sdcResponse.taxItems.forEach(taxItem => {
            lines.push(`[L]${taxItem.label}[C]${taxItem.categoryName}[C]${Money.parse(taxItem.rate).toFormat(priceFormat)}%[R]${Money.parse(taxItem.amount).toFormat(priceFormat)}`)
        })

        lines.push(`[L][D-]`)

        const totalTax = sdcResponse.taxItems.reduce((acc, cur) => {
            const taxRateAmount = Money.parse(cur.amount)
            return acc.add(Money.parse(taxRateAmount.toRoundedUnit(2)))
        }, Money.parse(0))

        lines.push(`[L]${latToCyr(i18n.t('receipt.taxes.total'))}:[S]${totalTax.toFormat(priceFormat)}`)

        lines.push(`[L][D=]`)
        lines.push(`[L]${latToCyr(i18n.t('receipt.sdc.time'))}:[S]${moment(sdcResponse.sdcDateTime).tz('Europe/Belgrade').format('DD.MM.YYYY. HH:mm:ss')}`)
        lines.push(`[L]${latToCyr(i18n.t('receipt.sdc.number'))}:[S]${sdcResponse.invoiceNumber}`)
        lines.push(`[L]${latToCyr(i18n.t('receipt.sdc.counter'))}:[S]${sdcResponse.invoiceCounter}`)
        lines.push(`[L][D=]`)

        if(invoice.invoiceType === 'Copy' && invoice.transactionType === 'Refund') {
            lines.push(`[L]`)
            lines.push(`[L][D_]${latToCyr(i18n.t('receipt.buyer_signature'))}: `)
            lines.push(`[L]`)
        }

        if(['Normal','Advance'].includes(invoice.invoiceType)) {
            lines.push(`[C][D=] ${latToCyr(i18n.t('receipt.fiscal_receipt_end'))} `)
        } else {
            lines.push(`[H12][C][D=] ${latToCyr(i18n.t('receipt.nonfiscal_receipt_end'))} `)
        }

        lines.push(`[L]`)

        if(order.getTable()) {
            lines.push(`[L]${i18n.t('receipt.table')}: ${order.getTable().name}`)
            lines.push(`[L][D-]`)
        }

        // Client
        let client = order.client || Client.find(order.client_id);
        if(client && !order.isIncoming()) {
            lines.push(`[L]${latToCyr(i18n.tc('general.buyer', 1))}: ${client.name}`)

            if(client.address) {
                lines.push(`[L]${client.address}${client.city ? (', ' + client.city) : ''}${client.country ? (', ' + client.country) : ''}`)
            }

            if(client.registration_number) {
                lines.push(`[L]${latToCyr(i18n.t('general.registration_number'))}: ${client.registration_number}`)
            }

            lines.push(`[L][D-]`)
        }

        if(order.note) {
            order.note.split('\n').forEach(l => lines.push(`[L]${l}`))
            lines.push(`[L][D-]`)
        }

        if(receiptNote) {
            receiptNote.split('\n').forEach(l => lines.push(`[L]${l}`))
            lines.push(`[L][D-]`)
        }

        if(order.refersToAdvance()) {
            const advanceRefund = order.refers_to[0];
            if(advanceRefund) {
                const advanceRefundInvoice = advanceRefund.register_request || advanceRefund.getRegisterRequestSr();
                lines.push(`[L]${latToCyr(i18n.t('receipt.last_advance_receipt'))}:`)
                lines.push(`[L]${advanceRefundInvoice.referentDocumentNumber} ${moment(advanceRefundInvoice.referentDocumentDT).tz('Europe/Belgrade').format('DD.MM.YYYY.')}`)
                lines.push(`[L][D-]`)
            }
        }

        if(order.getDiscountAmount().toUnit() > 0) {
            lines.push(`[L]${latToCyr(i18n.t('receipt.discount_note'))}:[S]${order.getDiscountAmount().toFormat(priceFormat)}`)
            lines.push(`[L][D-]`)
        }

        if(order.getFlag('type').includes('noCompensation')) {
            lines.push(`[L]* ${latToCyr(i18n.t('receipt.no_compensation'))}`)
            lines.push(`[L][D-]`)
        }

        return lines
    }

    unpaidOrderToLinesSr(order, options = {}) {
        const lines = []

        const curShift = Shift.current();
        const curShiftOrders = curShift ? curShift.getOrders() : [];
        const orderNumber = curShiftOrders.length + 1

        if(options.printingSector) {
            lines.push(`[C]${orderNumber} # ${i18n.t('products.printing_sector.' + options.printingSector)}`)
            lines.push('[L]')
        }

        lines.push('[L][D-]')

        const table = order.getTable()
        if(table) {
            const sector = order.getSector()
            const tableLabel = table.name + (sector ? ` (${sector.name})` : '')
            const guestId = order.getGuestId();
            const guestLabel = guestId !== null ? ` / G${guestId + 1}` : ''
            lines.push(`[L]${i18n.t('general.table')}: ${tableLabel}${guestLabel}`)
        }

        lines.push(`[L]${i18n.t('general.time')}: ${this.formatDateTimeHuman(order.getLastActivityAt())}`)
        lines.push(`[L]${i18n.t('general.issued_by')}:[B]${order.getUser().name}`)
        lines.push('[L][D-]')

        // Product Items
        let productItems = order.isSummary()
            ? order.paysForOrders().flatMap(order => order.items)
            : order.items

        productItems = productItems.map((item, index) => {
            item.index = index;
            return item;
        })

        // Item separators
        const separators = order.getItemSeparators();

        if(options.printingSector && options.printingSector !== 'order') {
            productItems = productItems.filter(item => {
                const product = item.getProduct();
                return !product || product.printing_sector === options.printingSector
            })
        }

        lines.push('[L]')

        productItems.forEach((item, index) => {
            if(index !== 0 && separators.length && separators[0] <= item.index) {
                lines.push('[L][D-]');
                lines.push('[L]')
                separators.shift();
            }

            lines.push(`[H22][L]${item.getCount()} x ${item.name}`)

            if(item.comments.length) {
                lines.push('[L]')
            }

            item.comments.forEach(comment => {
                lines.push(`[L]- ${comment}`)
            })

            lines.push('[L]')
        });

        return lines
    }

    orderToText(order, options = {}) {
        return this.orderToLines(order, options)
            .map(l => this.textGen.line(l))
            .join('\n')
    }

    getOrderSummary(order) {
        if(order.isSummary() && !order.paysForOrders()) {
            return { toPay: order.getToPay() }
        }

        return {
            exemptFromTax: order.getExemptFromTax(),
            priceBeforeTax: order.getPriceBeforeTax(),
            price: order.getPrice(),
            discountAmount: order.getDiscountAmount(),
            toPay: order.getToPay(),
        }
    }

    getOrderPaymentTypes(order) {
        const paymentTypes = [
            'cash',
            'credit_card',
            'invoice',
            'credit_card_business',
            'advance',
            'check',
            'voucher',
            'mobile_money',
            'other_noncash'
        ]

        return paymentTypes.map(type => {
            return {
                label: i18n.t(`orders.payment_types.${type}`),
                value: Money.parse(order['paid_' + type]),
            }
        }).filter(type => type.value.toUnit() != 0)
    }

    formatDateTime(date) {
        if(!(date instanceof moment)) {
            date = moment(date)
        }

        return ['me', 'sr', 'ba'].includes(locale)
            ? date.format('DD.MM.YYYY. HH:mm:ss')
            : date.format('YYYY/MM/DD HH:mm:ss')
    }

    formatDateTimeHuman(date) {
        if(!(date instanceof moment)) {
            date = moment(date)
        }

        return ['me', 'sr', 'ba'].includes(locale)
            ? date.format('D MMM HH:mm')
            : date.format('D MMM HH:mm')
    }

    t(prefix, key) {
        return i18n.t(`${prefix}.${key}`) !== `${prefix}.${key}`
            ? i18n.t(`${prefix}.${key}`)
            : key
    }
}
